// Tab "Wartung" (W1-W15, W20, W23 of APP-PARITY.md): flush, backflush and cleaning (armed remotely, run with S3), the
// descaling programme, the drip tray, counters; the device's view for Einstellungen → System (since 23.10.2026). Water
// actions only with the water level sensor; every start asks first (catalog confirm).
import {
  backflushProgressNote,
  careOf,
  descaleNote,
  liveOf,
  opOf,
  wifiQuality,
  type Note,
} from '../status.js';
import { localTime, presenceOf } from '../calm.js';
import { backflushTimes } from './device.js';
import { actionView, textOf, type ActionView, type ScreenContext } from './context.js';
import { coffeeText, dateText, dotJoin, formatNumber, lastDayText } from './format.js';
import { typeset } from '../i18n.js';
import { noteView, type NoteView } from './machine.js';

const machineState = (ctx: ScreenContext) => {
  const live = liveOf(ctx.view, ctx.now);
  const op = opOf(ctx.view);
  return { live, op, state: live?.state ?? op.state };
};

// ------------------------------------------------------------------------------------------------ flush

export interface FlushView {
  title: string;
  what: string;
  /** "Spülstoß 2 von 3", "erst Schalter aus", "nur mit Sensor" … */
  status: string | null;
  running: boolean;
  start: ActionView | null;
  stop: ActionView | null;
  /** settings in the card: warm-up flush only with the sensor */
  warmupFlush: boolean;
}

export function flushView(ctx: ScreenContext): FlushView {
  const { live, op, state } = machineState(ctx);
  const stage = live?.warmup ?? op.warmup?.stage ?? 0;
  const pulse = live?.pulse ?? op.warmup?.pulse ?? 0;
  const pulses = live?.pulses ?? op.warmup?.pulses ?? 0;
  const running = state === 25 || stage === 2 || stage === 3;
  const sw = live?.sw ?? op.switch ?? false;
  const can = state === 10 && !sw;
  const status = !ctx.watertank
    ? ctx.tr('flushNeedsSensor')
    : running
      ? ctx.tr(stage === 3 ? 'flushPause' : 'flushPulse', { n: stage === 3 ? pulse + 1 : pulse, m: pulses })
      : sw
        ? ctx.tr('flushSwitch')
        : stage === 1
          ? careOf(ctx.view)?.due.drip && ctx.num('drip.capacity') > 0
            ? ctx.tr('flushWaitsDrip')
            : ctx.tr('flushPending', { n: ctx.num('brew.warmup_flush_wait', 2) })
          : !can && ctx.controllable
            ? ctx.tr('flushBusy')
            : null;
  return {
    title: ctx.tr('sFlush'),
    what: ctx.tr('flushWhat'),
    status,
    running,
    start:
      ctx.watertank && !running
        ? actionView(ctx, 'flush.start', {
            label: 'flushNow',
            args: { pulses: 3 },
            when: can,
            ask: { title: 'flushConfirmTitle', text: 'flushConfirm', ok: 'flushGo' },
          })
        : null,
    stop: ctx.watertank && running ? actionView(ctx, 'flush.stop', { label: 'flushStop' }) : null,
    warmupFlush: ctx.watertank,
  };
}

// ------------------------------------------------------------------------------------------------ backflush

export interface BackflushView {
  title: string;
  /** "letzter Backflush vor 12 Bezügen · 03.10.2026" */
  since: string | null;
  notes: NoteView[];
  due: boolean;
  /** shots since the last one against "Erinnern nach" (null without a reminder) */
  bar: { fraction: number; due: boolean } | null;
  start: ActionView | null;
  stop: ActionView | null;
  cleanStart: ActionView | null;
  cleanStop: ActionView | null;
}

/** Backflush and cleaning modes as target states (backflush.set {armed}, cleaning.set {armed}); S3 runs them. */
export function backflushView(ctx: ScreenContext): BackflushView {
  const { live, op, state } = machineState(ctx);
  const care = careOf(ctx.view);
  const bf = op.backflush ?? { armed: false };
  const cleaner = live?.clean ?? bf.cleaner ?? 0;
  const armed = bf.armed;
  const cycle = live?.bfc ?? bf.cycle ?? 0;
  const done = live?.bfd ?? bf.done ?? false;
  const progress = backflushProgressNote(ctx.view, ctx.now, backflushTimes(ctx));
  const notes: Note[] = [
    ...(cleaner > 0 ? [{ key: `clean${cleaner}`, tone: 'water' as const }] : []),
    ...(progress ? [progress] : []),
    ...(done ? [{ key: 'bfDoneNote', tone: 'info' as const }] : []),
    ...(armed && cleaner === 0 && cycle === 0 && !done
      ? [{ key: bf.by ? 'bfActiveBy' : 'bfActive', tone: 'info' as const, vars: { by: bf.by ?? '' } }]
      : []),
  ];
  const idle = state === 10;
  const every = ctx.num('backflush.remind_after');
  return {
    title: ctx.tr('sBf'),
    since: care
      ? `${ctx.tr('bfSince', { n: care.shotsSinceBackflush })}${care.backflushAt ? ` · ${dateText(care.backflushAt, ctx.lang)}` : ''}`
      : null,
    notes: notes.map((n) => noteView(ctx, n)),
    due: care?.due.backflush ?? false,
    bar: care && every > 0 ? { fraction: care.shotsSinceBackflush / every, due: care.due.backflush } : null,
    start: !armed
      ? actionView(ctx, 'backflush.set', {
          label: 'bfStart',
          args: { armed: true },
          when: idle,
          ask: { title: 'bfStartQ', text: 'bfConfirm' },
        })
      : null,
    stop:
      armed && cleaner === 0
        ? actionView(ctx, 'backflush.set', { label: 'bfStop', args: { armed: false } })
        : null,
    cleanStart: !armed
      ? actionView(ctx, 'cleaning.set', {
          label: 'cleanStart',
          args: { armed: true },
          when: idle,
          ask: { title: 'cleanStartQ', text: 'cleanConfirm' },
        })
      : null,
    cleanStop:
      cleaner > 0 ? actionView(ctx, 'cleaning.set', { label: 'cleanStop', args: { armed: false } }) : null,
  };
}

// ------------------------------------------------------------------------------------------------ descaling

export interface DescaleView {
  title: string;
  /** "~38,2 l seit dem Entkalken am 01.09.2026" */
  since: string | null;
  bar: { fraction: number; due: boolean } | null;
  needsSensor: string | null;
  status: { text: string; tone: 'info' | 'water' | 'warn' } | null;
  running: boolean;
  start: ActionView | null;
  next: ActionView | null;
  stop: ActionView | null;
  descaled: ActionView;
  how: { title: string; steps: string[]; source: string };
  /**
   * board "Entkalken": the water since the last descaling against the reminder ("~31 l" "von 40 l", the bar) and
   * when it was ("zuletzt am 20.08."); null before the machine reported its counters
   */
  water: { label: string; text: string; of: string | null; fraction: number; last: string | null } | null;
  /** the programme's steps with the one running now (board: "Vorbereiten", "Abkühlen", "Entkalken", "Klarspülen") */
  program: {
    title: string;
    info: string;
    duration: string;
    steps: { n: number; name: string; text: string; now: boolean; done: boolean }[];
  };
}

export function descaleView(ctx: ScreenContext): DescaleView {
  const { live, op, state } = machineState(ctx);
  const care = careOf(ctx.view);
  const litres = ctx.num('descale.litres');
  const water = (care?.waterMl ?? 0) / 1000;
  const phase = live?.dsc ?? op.descale?.phase ?? 0;
  const running = phase > 0 && phase < 7;
  const waiting = phase === 3 || phase === 5;
  const note = descaleNote(op, live);
  return {
    title: ctx.tr('sDescale'),
    since: care
      ? care.descaledAt
        ? ctx.tr('descaleSince', {
            l: formatNumber(water, 1, ctx.lang),
            d: dateText(care.descaledAt, ctx.lang),
          })
        : ctx.tr('descaleCounted', { l: formatNumber(water, 1, ctx.lang) })
      : null,
    bar: litres > 0 ? { fraction: water / litres, due: care?.due.descale ?? false } : null,
    needsSensor: ctx.watertank ? null : ctx.tr('dsNeedsSensor'),
    status: note ? { text: textOf(ctx, note.key, note.vars), tone: note.tone } : null,
    running,
    start:
      ctx.watertank && !running
        ? actionView(ctx, 'descale.start', {
            label: 'dsStart',
            when: state === 10 || state === 60,
            ask: { title: 'dsStartQ', text: 'dsConfirm' },
          })
        : null,
    next:
      ctx.watertank && waiting
        ? actionView(ctx, 'descale.next', { label: 'dsNext', expect: { 'descale.phase': phase } })
        : null,
    stop:
      ctx.watertank && running
        ? actionView(ctx, 'descale.stop', {
            label: 'dsStop',
            ask: { title: 'dsStopQ', text: 'dsReallyStop', danger: true },
          })
        : null,
    descaled: actionView(ctx, 'care.descaled', {
      label: 'descaled',
      when: !running,
      ask: { title: 'descaledQ', text: 'reallyDescaled' },
    }),
    how: {
      title: ctx.tr('descaleHow'),
      steps: [ctx.tr('descale1'), ctx.tr('descale2'), ctx.tr('descale3'), ctx.tr('descale4')],
      source: ctx.tr('descaleSrc'),
    },
    water: care
      ? {
          label: ctx.tr('dsSince'),
          // an estimate with its tilde; nothing run through yet is a plain 0
          text: `${water > 0 ? '~' : ''}${formatNumber(water, 0, ctx.lang)}\u00a0l`,
          of: litres > 0 ? ctx.tr('dsOf', { l: litres }) : null,
          fraction: litres > 0 ? water / litres : 0,
          last: care.descaledAt
            ? ctx.tr('dsLast', { d: lastDayText(care.descaledAt * 1000, ctx.lang, ctx.now, ctx.zone) })
            : null,
        }
      : null,
    program: {
      title: ctx.tr('dsProgram'),
      info: ctx.tr('dsProgramInfo'),
      duration: ctx.tr('dsDuration'),
      // the step a phase belongs to: 1 cooling, 2-4 rounds and the rest, 5-6 rinsing (status.ts descaleNote)
      steps: (['dsStep1', 'dsStep2', 'dsStep3', 'dsStep4'] as const).map((key, i) => {
        const step = running ? (phase === 1 ? 2 : phase <= 4 ? 3 : 4) : 0;
        return {
          n: i + 1,
          name: ctx.tr(key),
          text: ctx.tr(`${key}Text`),
          now: step === i + 1,
          done: step > i + 1,
        };
      }),
    },
  };
}

// ------------------------------------------------------------------------------------------------ drip, counters, device

export interface DripView {
  title: string;
  /** "~320 ml von 600 ml" while a capacity is set */
  now: string | null;
  bar: { fraction: number; due: boolean } | null;
  emptied: ActionView;
}

export function dripView(ctx: ScreenContext): DripView {
  const care = careOf(ctx.view);
  const cap = ctx.num('drip.capacity');
  return {
    title: ctx.tr('sDrip'),
    now: care && cap > 0 ? ctx.tr('dripNow', { ml: care.dripMl, cap }) : null,
    bar: care && cap > 0 ? { fraction: care.dripMl / cap, due: care.due.drip } : null,
    emptied: actionView(ctx, 'drip.emptied', { label: 'dripEmptied' }),
  };
}

export interface CountsView {
  title: string;
  items: { key: 'today' | 'week' | 'total' | 'coffee'; value: string; label: string }[];
}

export function countsView(ctx: ScreenContext): CountsView | null {
  const care = careOf(ctx.view);
  if (!care) return null;
  return {
    title: ctx.tr('sCount'),
    items: [
      { key: 'today', value: String(care.today), label: ctx.tr('cToday') },
      { key: 'week', value: String(care.week), label: ctx.tr('cWeek') },
      { key: 'total', value: String(care.total), label: ctx.tr('cTotal') },
      { key: 'coffee', value: coffeeText(care.coffeeGrams, ctx.lang), label: ctx.tr('cCoffee') },
    ],
  };
}

/** A line of the device card: label and value ("Läuft seit | 3 h 12 min"). */
export interface DeviceFact {
  key: 'fw' | 'uptime' | 'lastStart' | 'wifi';
  label: string;
  value: string;
}

export interface DeviceView {
  title: string;
  /**
   * firmware ("–" unknown), running for and WLAN while the machine is reachable (state/health), the last start:
   * label and value like a bean's head card (review 19.10.2026: one line of text and two grey lines)
   */
  facts: DeviceFact[];
  /** "neue Firmware wird geprüft" */
  pending: string | null;
  /** "Das letzte Update ist nicht gestartet, die vorige Firmware läuft." after a rollback */
  rolledBack: string | null;
  restart: ActionView;
}

/** "3 h 12 min", "12 min" */
const upText = (s: number): string => {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h ? `${h} h ${m} min` : `${m} min`;
};

export function deviceView(ctx: ScreenContext): DeviceView {
  const boot = ctx.view.groups.boot?.data as
    { cause?: string; fw?: string; fwState?: string; rolledBackFrom?: string | null } | undefined;
  const fw = boot?.fw ?? (ctx.view.profile?.data as { fw?: string } | undefined)?.fw;
  const presence = presenceOf(ctx.view, ctx.now);
  const reachable = presence === 'online' || presence === 'reconnecting';
  const healthGroup = ctx.view.groups.health;
  const health = healthGroup?.data as { up?: number; rssi?: number } | undefined;
  const up =
    reachable && healthGroup && typeof health?.up === 'number'
      ? health.up + Math.max(0, ctx.now - localTime(ctx.view, healthGroup.receivedAt)) / 1000
      : null;
  const rssi = liveOf(ctx.view, ctx.now)?.rssi ?? health?.rssi;
  const q = wifiQuality(rssi);
  const rolledBack =
    boot?.cause === 'rollback' || (typeof boot?.rolledBackFrom === 'string' && !!boot.rolledBackFrom);
  const labels = { fw: 'fwVersion', uptime: 'runningFor', lastStart: 'lastStart', wifi: 'wifi' } as const;
  const fact = (key: DeviceFact['key'], value: string | null): DeviceFact[] =>
    value ? [{ key, label: ctx.tr(labels[key]), value: typeset(value) }] : [];
  return {
    title: ctx.tr('sDevice'),
    // in the board's order; what is not known stays out (WRITING.md 0a: no "–", no "unbekannt")
    facts: [
      ...fact('fw', fw ?? null),
      ...fact('uptime', up !== null ? upText(Math.floor(up)) : null),
      ...fact(
        'wifi',
        q && reachable && typeof rssi === 'number' ? dotJoin([ctx.tr(q), `−${-rssi}\u00a0dBm`]) : null,
      ),
      ...fact(
        'lastStart',
        boot?.cause && boot.cause !== 'unknown' ? textOf(ctx, `cause_${boot.cause}`) : null,
      ),
    ],
    pending: boot?.fwState === 'pending' ? ctx.tr('fwPending') : null,
    rolledBack: rolledBack
      ? ctx.tr(boot?.rolledBackFrom ? 'fwRolledBackFrom' : 'fwRolledBack', { v: boot?.rolledBackFrom ?? '' })
      : null,
    // the question as the title, the button with the same verb (WRITING.md 2, 23.10.2026)
    restart: actionView(ctx, 'restart', {
      label: 'restart',
      ask: { title: 'restartQ', text: 'restartText' },
    }),
  };
}

// ------------------------------------------------------------------------------------------------ prototype "Wartung"

/** "etwa am Freitag", "etwa morgen", "Anfang Dezember": when something that many days ahead will be due. */
export function aboutWhen(ctx: ScreenContext, days: number): string {
  if (days <= 0) return ctx.tr('today');
  if (days === 1) return ctx.tr('aboutTomorrow');
  const at = new Date(ctx.now + days * 86_400_000);
  const loc = ctx.lang === 'de' ? 'de-DE' : 'en-GB';
  if (days < 7)
    return ctx.tr('aboutWeekday', { d: at.toLocaleDateString(loc, { weekday: 'long', timeZone: ctx.zone }) });
  const day = Number(at.toLocaleDateString('en-GB', { day: 'numeric', timeZone: ctx.zone }));
  const month = at.toLocaleDateString(loc, { month: 'long', timeZone: ctx.zone });
  return ctx.tr(day <= 10 ? 'earlyMonth' : day <= 20 ? 'midMonth' : 'lateMonth', { m: month });
}

export interface DueItem {
  key: 'drip' | 'backflush' | 'descale';
  name: string;
  /** "in 12 Bezügen, etwa am Freitag", "fast voll, 540 von 600 ml" */
  due: string;
  fraction: number;
  /** due now: the bar in the accent colour, the item first */
  now: boolean;
}

/**
 * What is due (prototype "Wartung", "Als Nächstes"): the drip tray, the backflush and the descaling with when they will
 * be due, estimated from the shots of the week and the water since the last descaling; the most urgent first.
 */
export function dueItems(ctx: ScreenContext): DueItem[] {
  const care = careOf(ctx.view);
  if (!care) return [];
  const n = (v: number, d: number) => formatNumber(v, d, ctx.lang);
  const out: DueItem[] = [];
  const cap = ctx.num('drip.capacity');
  if (cap > 0) {
    const f = care.dripMl / cap;
    out.push({
      key: 'drip',
      name: ctx.tr('sDrip'),
      due: care.due.drip
        ? ctx.tr('dripAlmostFull', { ml: care.dripMl, cap })
        : care.dripMl <= 0
          ? ctx.tr('dripEmpty')
          : ctx.tr('dripNow', { ml: care.dripMl, cap }),
      fraction: f,
      now: care.due.drip,
    });
  }
  const every = ctx.num('backflush.remind_after');
  if (every > 0) {
    const left = every - care.shotsSinceBackflush;
    const perDay = care.week / 7;
    out.push({
      key: 'backflush',
      name: ctx.tr('sBf'),
      due:
        left <= 0
          ? ctx.tr('bfDueNow', { n: care.shotsSinceBackflush })
          : perDay > 0
            ? ctx.tr('bfDueIn', { n: left, when: aboutWhen(ctx, Math.ceil(left / perDay)) })
            : ctx.tr('bfDueInN', { n: left }),
      fraction: care.shotsSinceBackflush / every,
      now: care.due.backflush,
    });
  }
  const litres = ctx.num('descale.litres');
  if (litres > 0) {
    const water = care.waterMl / 1000;
    const left = litres - water;
    const days = care.descaledAt ? (ctx.now / 1000 - care.descaledAt) / 86_400 : 0;
    const perDay = days >= 3 ? water / days : 0;
    out.push({
      key: 'descale',
      name: ctx.tr('sDescale'),
      due:
        left <= 0
          ? ctx.tr('dsDueNow', { l: n(water, 0) })
          : perDay > 0
            ? ctx.tr('dsDueIn', { l: n(left, 0), when: aboutWhen(ctx, Math.ceil(left / perDay)) })
            : ctx.tr('dsDueInL', { l: n(left, 0) }),
      fraction: water / litres,
      now: care.due.descale,
    });
  }
  return out.sort((a, b) => Number(b.now) - Number(a.now) || b.fraction - a.fraction);
}

/** The counters (prototype "Wartung"): every shot so far, the coffee, the water since the last descaling. */
export function counterFacts(ctx: ScreenContext): { key: string; label: string; value: string }[] | null {
  const care = careOf(ctx.view);
  if (!care) return null;
  return [
    {
      key: 'total',
      label: ctx.tr('shotsLabel'),
      value: care.total.toLocaleString(ctx.lang === 'de' ? 'de-DE' : 'en-GB'),
    },
    { key: 'coffee', label: ctx.tr('cCoffee'), value: coffeeText(care.coffeeGrams, ctx.lang) },
    {
      key: 'water',
      label: ctx.tr('sinceDescale'),
      value: `${formatNumber(care.waterMl / 1000, 0, ctx.lang)}\u00a0l`,
    },
  ];
}
